"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const tslib_1 = require("tslib");
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = tslib_1.__importStar(require("react"));
const classnames_1 = tslib_1.__importDefault(require("classnames"));
const head_1 = tslib_1.__importDefault(require("lodash/fp/head"));
const negate_1 = tslib_1.__importDefault(require("lodash/fp/negate"));
const delay_1 = tslib_1.__importDefault(require("lodash/fp/delay"));
const ActionBarOverlay_1 = tslib_1.__importDefault(require("./ActionBarOverlay"));
const ActionBarItemPopoverContent_1 = tslib_1.__importDefault(require("./ActionBarItemPopoverContent"));
const ActionBarItemIcon_1 = tslib_1.__importDefault(require("./ActionBarItemIcon"));
const ActionBarItemList_1 = tslib_1.__importDefault(require("./ActionBarItemList"));
const ActionBarItemListItem_1 = tslib_1.__importDefault(require("./ActionBarItemListItem"));
const ActionBarItemListSeparator_1 = tslib_1.__importDefault(require("./ActionBarItemListSeparator"));
const OverlayTrigger_1 = tslib_1.__importDefault(require("../overlay/OverlayTrigger"));
const Dialog_1 = tslib_1.__importDefault(require("../dialog/Dialog"));
const EVENT = 'mousedown';
const CLASSNAME_SHOW = 'show';
const CLASSNAME_OFFSCREEN = 'position-offscreen';
const DEFAULT_POPOVER_WIDTH = 250;
const POPOVER_CLOSE_DELAY_IN_MS = 200;
const SMALL_RESOLUTION_THRESHOLD_IN_PX = 580;
const getRandomString = () => (Math.random() + 1).toString(36).toUpperCase().substring(2);
const hasClass = (element, className) => element.classList.contains(className);
const hasNotClass = (0, negate_1.default)(hasClass);
const isNotChildNodeOf = (childNode, parentNode) => !parentNode.contains(childNode);
const checkIfSmallResolution = () => {
    const [isSmallResolution, setIsSmallResolution] = (0, react_1.useState)(false);
    // We only execute this once. Before it was re-rendered on every change.
    // Users probably don't switch between display sizes that often therefore it probably makes sense to
    // avoid the re-rendering or applying an event listener.
    (0, react_1.useEffect)(() => {
        const header = (0, head_1.default)(document.getElementsByClassName('ApplicationHeader'));
        const isSmall = header ? header.offsetWidth < SMALL_RESOLUTION_THRESHOLD_IN_PX : false;
        setIsSmallResolution(isSmall);
    }, []);
    return isSmallResolution;
};
const isActionBarItemPopover = (child) => child.type && child.type.displayName === ActionBarItemPopoverContent_1.default.displayName;
const isActionBarItemIcon = (child) => child.type && child.type.displayName === ActionBarItemIcon_1.default.displayName;
const ActionBarItemBase = (props) => {
    const { id = getRandomString(), className, mobileDialogBodyClassName = '', children, popoverWidth = DEFAULT_POPOVER_WIDTH, hidePopoverOnClick = true } = props, remainingProps = tslib_1.__rest(props, ["id", "className", "mobileDialogBodyClassName", "children", "popoverWidth", "hidePopoverOnClick"]);
    const [isShown, setIsShown] = (0, react_1.useState)(false);
    const clickOutsideRef = (0, react_1.useRef)(null);
    (0, react_1.useLayoutEffect)(() => {
        const listener = (event) => {
            if (!clickOutsideRef || !clickOutsideRef.current || !isShown) {
                return;
            }
            // Since the popover component is based on React Portal and might be offscreen, we need to use
            // old-school approach and query the DOM for the item ID.
            const popoverEl = document.getElementById(id);
            if (!popoverEl) {
                return;
            }
            // Abort when the ActionBarItemIcon itself has been clicked as there is a toggle function
            // applied to the icon that takes care of opening and closing
            const hasIconClickedToClose = event.target.offsetParent === clickOutsideRef.current;
            if (hasIconClickedToClose) {
                return;
            }
            // Handle click outside the popover to close it
            const isClickOutsidePopover = isNotChildNodeOf(event.target, popoverEl);
            const isPopoverVisible = hasClass(popoverEl, CLASSNAME_SHOW) && hasNotClass(popoverEl, CLASSNAME_OFFSCREEN);
            if (isPopoverVisible && isClickOutsidePopover) {
                setIsShown(false);
                return;
            }
            // Handle click inside the popover.
            // Delay the closing of the popover to execute possible actions from within the popover
            // like clicks on links or buttons
            if (hidePopoverOnClick) {
                (0, delay_1.default)(POPOVER_CLOSE_DELAY_IN_MS)(() => setIsShown(false));
            }
        };
        document.addEventListener(EVENT, listener);
        return () => {
            document.removeEventListener(EVENT, listener);
        };
    }, [clickOutsideRef, id, isShown]);
    const classes = (0, classnames_1.default)('ActionBarItem', className);
    const isSmallScreen = checkIfSmallResolution();
    const onToggle = (0, react_1.useCallback)(() => setIsShown(!isShown), [setIsShown, isShown]);
    // The children depend on each other, that's why it's easier to convert them once to an array
    // and then pick the correct elements. In most cases there are only 2 or 3 elements in the children
    // array so the looping shouldn't be too worrisome.
    const childrenAsList = react_1.default.Children.toArray(children);
    const itemPopover = childrenAsList.find(isActionBarItemPopover);
    const itemIcon = childrenAsList.find(isActionBarItemIcon);
    if (!itemPopover) {
        return (0, jsx_runtime_1.jsx)("div", Object.assign({ className: classes }, { children: children }));
    }
    const { useOffscreen = false, title } = itemPopover.props;
    // Filter out the popover and icon component from the list of children as they are handled separately
    const childrenWithoutPopover = childrenAsList.filter(child => !isActionBarItemPopover(child));
    const childrenWithoutPopoverAndIcon = childrenWithoutPopover.filter(child => !isActionBarItemIcon(child));
    if (isSmallScreen) {
        return ((0, jsx_runtime_1.jsxs)("div", Object.assign({}, remainingProps, { className: classes, ref: clickOutsideRef }, { children: [react_1.default.cloneElement(itemIcon, { onClick: onToggle }), (0, jsx_runtime_1.jsx)("div", Object.assign({ onClick: () => setIsShown(false) }, { children: (0, jsx_runtime_1.jsx)(Dialog_1.default, { show: isShown, onHide: () => setIsShown(false), body: itemPopover, bodyClassName: mobileDialogBodyClassName, title: title }) })), childrenWithoutPopoverAndIcon] })));
    }
    const overlay = ((0, jsx_runtime_1.jsx)(ActionBarOverlay_1.default, Object.assign({ id: id, title: title, width: popoverWidth, preRender: useOffscreen, show: isShown }, { children: itemPopover }), 'child'));
    return ((0, jsx_runtime_1.jsxs)("div", Object.assign({}, remainingProps, { className: classes, ref: clickOutsideRef }, { children: [(0, jsx_runtime_1.jsx)(OverlayTrigger_1.default, Object.assign({ onToggle: onToggle, show: isShown || useOffscreen, placement: OverlayTrigger_1.default.BOTTOM_END, overlay: overlay, trigger: 'click', rootClose: false, popperConfig: {
                    modifiers: [
                        {
                            name: 'offset',
                            options: {
                                offset: [0, 5],
                            },
                        },
                    ],
                } }, { children: itemIcon })), childrenWithoutPopoverAndIcon] })));
};
const ActionBarItem = (0, react_1.memo)(ActionBarItemBase);
// Define static variables on the component type
ActionBarItem.Icon = ActionBarItemIcon_1.default;
ActionBarItem.Popover = ActionBarItemPopoverContent_1.default;
ActionBarItem.List = ActionBarItemList_1.default;
ActionBarItem.ListItem = ActionBarItemListItem_1.default;
ActionBarItem.ListSeparator = ActionBarItemListSeparator_1.default;
exports.default = ActionBarItem;
